import styled, { css } from 'styled-components';
import { FIELD_MAX_HEIGHT, ELEMENT_MAX_WIDTH } from 'styles';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import List from 'components/List/styles';
import { DEEP_BORDER_BOX } from 'common';

const {
  SPACING,
  TYPOGRAPHY,
  LINE_HEIGHT,
} = Tokens;

const SPACING_18 = '1.125rem';

const TEXT_TRUNCATE_STYLE = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const GET_BORDER_COLOR = props =>
  props.disabled ? props.theme.disabled.border
    : props.error ? props.theme.error.border
      : props.hovered ? props.theme.hover.border
        : props.focused ? props.theme.focused.border
          : props.theme.border;

const GET_OUTLINED_FOCUSED_STYLE = props => `inset 0rem 0rem 0rem ${SPACING.SPACING_2} ${GET_BORDER_COLOR(props)}`;
const GET_UNDERLINED_FOCUSED_STYLE = props => `inset 0rem -${SPACING.SPACING_2} 0rem 0rem ${GET_BORDER_COLOR(props)}`;
const GET_FOCUSED_BORDER_STYLE = props => props.variant === 'outlined'
  ? GET_OUTLINED_FOCUSED_STYLE(props)
  : GET_UNDERLINED_FOCUSED_STYLE(props);

const GET_OUTLINED_HOVER_STYLE = props => `inset 0rem 0rem 0rem ${SPACING.SPACING_2} ${GET_BORDER_COLOR(props)}`;
const GET_UNDERLINED_HOVER_STYLE = props => `inset 0rem -${SPACING.SPACING_2} 0rem 0rem ${GET_BORDER_COLOR(props)}`;
const GET_HOVER_BORDER_STYLE = props => props.variant === 'outlined'
  ? GET_OUTLINED_HOVER_STYLE(props)
  : GET_UNDERLINED_HOVER_STYLE(props);

const SelectItemWrapper = styled.div`
  ${DEEP_BORDER_BOX}
  ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
  display: flex;
  flex-direction: column;
  cursor: pointer;
  height: ${SPACING.SPACING_32};
  ${({ $multiSelect }) => $multiSelect && css`
    height: auto;
    min-height: ${SPACING.SPACING_32};
    flex-wrap: wrap;
    align-content: flex-start;
    flex-direction: row;
  `};
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${props => props.maxWidth ? css`
    max-width: ${ELEMENT_MAX_WIDTH};
  ` : css`
    max-width: 100%;
  `}
  color: ${props => props.theme.content};
  background: ${props => props.theme.background};
  ${props => css`
    box-shadow: ${props.readOnly
    ? 'none'
    : props.variant === 'outlined'
      ? `inset 0rem 0rem 0rem 0.0625rem ${GET_BORDER_COLOR(props)}`
      : `inset 0rem -0.0625rem 0rem 0rem ${GET_BORDER_COLOR(props)}`};
    padding: ${props.variant === 'outlined' ? `0.1875rem ${SPACING.SPACING_24} 0.1875rem ${SPACING.SPACING_8}` : '0.1875rem 1.5rem 0.1875rem 0'};
    ${({ RTL }) => RTL && css`
      padding-right: ${props.variant === 'outlined' ? '0.5rem' : '0'};
      padding-left: 1.5rem;
    `};
  `}
  ${TYPOGRAPHY.BODY}
  position: relative;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border: none;
  }
  &:hover {
    &::placeholder {
      color: ${props => props.theme.hover.placeholder}
    }
    ${({ disabled }) => !disabled &&
    css`
    ${props => css`box-shadow: ${props.readOnly ? 'none' : GET_HOVER_BORDER_STYLE({ ...props, hovered: true })};`}
      outline: none;
      border: none;
    `};
  }
  &:focus-within {
    ${({ disabled }) =>
    !disabled &&
    css`
      ${props => css`box-shadow: ${props.readOnly ? 'none' : GET_FOCUSED_BORDER_STYLE({ ...props, focused: true })};`}
      outline: none;
      border: none;
    `};
  }
  ${({ disabled }) => disabled && css`
    cursor: initial;
    background: ${props => props.theme.disabled.background};
    color: ${props => props.theme.disabled.content};
    ${props => css`box-shadow: ${props.readOnly ? 'none' : GET_FOCUSED_BORDER_STYLE({ ...props, disabled: true })};`}
  `};
  &::placeholder {
    color: ${props => props.theme.placeholder}
  }
  ${({ isOpen }) => isOpen && css`
    ${props => css`box-shadow: ${props.readOnly ? 'none' : GET_FOCUSED_BORDER_STYLE({ ...props, focused: true })};`}
    outline: none;
    border: none;
  `};
`;

const SelectItemButton = styled.div`
  display: flex;
  align-items: center;
  min-height: 1.625rem;
  outline: none;
  width: calc(100% - 0.5rem);
  height: ${({ $multiSelect }) => $multiSelect ? 'auto' : '1.625rem'};
  ${({ $multiSelect }) => $multiSelect && css`
    flex-wrap: wrap;
  `};
`;

const Placeholder = styled.div`
  color: ${props => props.theme.placeholder};
  ${TEXT_TRUNCATE_STYLE}
  ${TYPOGRAPHY.BODY}
  line-height: ${LINE_HEIGHT.LINE_HEIGHT_NONE};
  min-height: ${SPACING_18};
  padding: 0.3125rem 0;
`;

const SelectIndicators = styled.div`
  ${props => css` ${(props.RTL || props.$rtl || props.rtl) ? 'left: 0.625rem' : 'right: 0.625rem'};`}
  position: absolute;
  top: 0.25rem;
`;

const DropDownIndicator = styled.div`
  box-sizing: content-box;
  padding: 4px 0;
  ${props => css` ${props.hide ? 'display: none' : 'display: block'};`}
  ${({ $isOpen }) =>
    $isOpen && css` transform: rotate(-180deg);
  `};
  &:focus {
    outline: none;
    border: none;
  }
  width: ${SPACING.SPACING_16};
  height: ${SPACING.SPACING_16};
  svg {
    width: ${SPACING.SPACING_16};
    height: ${SPACING.SPACING_16};
  }
  svg * {
    pointer-events: none;
    fill: ${props => props.theme.icon};
  }
  ${({ disabled }) => disabled && css`
    svg {
      & * {
        fill: ${props => props.theme.disabled.icon};
      }
    }
  `};
`;

const ClearButton = styled.div`
  position: absolute;
  top: ${SPACING.SPACING_2};
  right: -${SPACING.SPACING_4};
  ${({ RTL }) => RTL && css`
    right: -1.125rem;
  `};
  box-sizing: content-box;
  ${({ hide }) => hide && css`
    opacity: 0;
    width: 0;
    height: 0;
    padding: 0;
  `};
`;

const ErrorContainer = styled.div`
  max-height: ${FIELD_MAX_HEIGHT};
  width: 100%;
  max-width: 100%;
  margin-top: ${SPACING.SPACING_4};
`;

const HiddenInput = styled.input`
  left: 0;
  width: 100%;
  bottom: 0;
  opacity: 0;
  position: absolute;
  pointer-events: none;
`;

const SelectItemInput = styled.input`
  color: ${props => props.theme.content};
  background: ${props => props.theme.background};
  display: flex;
  align-items: center;
  ${TYPOGRAPHY.BODY}
  padding: 0;
  outline: none;
  border: none;
  width: auto;
  height: 1.625rem;
  ${TEXT_TRUNCATE_STYLE}
  ${({ $isplaceholder }) => $isplaceholder && css`
    color: ${props => props.theme.placeholder}
  `};
  ::placeholder {
    color: ${props => props.theme.placeholder}
  }
  ${({ disabled }) => disabled && css`
    cursor: initial;
    background: ${props => props.theme.disabled.background};
    color: ${props => props.theme.disabled.content};
  `};
`;

const SelectListItem = styled(List.ListItemBasic)`
  ${({ searchSelection }) => searchSelection && css`
    ${props => css`box-shadow: ${props.readOnly ? 'none' : GET_FOCUSED_BORDER_STYLE({ ...props, focused: true })};`}
    padding: 0.125rem 1.375rem 0.125rem 0.5rem;
  `};
  ${({ disabled }) => disabled && css`
    pointer-events: none;
    svg * {
      pointer-events: none;
      fill: ${({ $theme }) => $theme && $theme.disabled.icon};
    }
  `};
`;

const SelectItemBaseText = styled.div`
  ${props => css`padding-right: ${(props.RTL || props.$rtl || props.rtl) ? '0' : `${SPACING.SPACING_12}`};`}
  ${TYPOGRAPHY.BODY}
  ${TEXT_TRUNCATE_STYLE}
  color: ${({ $theme }) => $theme && $theme.content};
  ${({ disabled }) => disabled && css`
    color: ${({ $theme }) => $theme.disabled.content};
  `};
`;

const SelectItemBaseIcon = styled.div`
  ${props => css`margin: ${(props.RTL || props.$rtl || props.rtl) ? `0 0 0 ${SPACING.SPACING_12}` : `0 ${SPACING.SPACING_12} 0 0`};`}
  ${({ disabled }) => disabled && css`
  svg {
    & * {
      fill: ${({ $theme }) => $theme.disabled.icon};
    }
  `};
  svg {
    & * {
      fill: ${({ $theme }) => $theme.icon};
  }
`;

const SelectItemTextContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  ${props => css`padding-right: ${(props.RTL || props.$rtl || props.rtl) ? `${SPACING.SPACING_16}` : `${SPACING.SPACING_12}`};`}
  ${TYPOGRAPHY.BODY}
`;

const SelectItemSubText = styled.div`
  ${TYPOGRAPHY.CAPTION}
  color: ${({ $theme }) => $theme && $theme.secondaryText};
  ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
  ${props => css`padding-right: ${(props.RTL || props.$rtl || props.rtl) ? '0' : `${SPACING.SPACING_12}`};`}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ disabled }) => disabled &&
    css`
      color: ${({ $theme }) => $theme.disabled.text};
  `};
`;

const SelectItemProgressIndicatorWrapper = styled.div`
  cursor: none;
  pointer-events: none;
  position: relative;
  padding-left: 1.5rem;
  width: calc(100% - 3.125rem);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SelectItemProgressIndicatorContent = styled.div`
  ${props => css`width: ${props.width ? props.width : '100%'};`}
  ${props => css`height: ${props.height ? props.height : '100%'};`}
  svg {
    & * {
      fill: inherit;
    };
  }
`;

const styles = {
  SelectItemWrapper,
  SelectItemButton,
  DropDownIndicator,
  ErrorContainer,
  SelectIndicators,
  ClearButton,
  SelectListItem,
  Placeholder,
  HiddenInput,
  SelectItemProgressIndicatorWrapper,
  SelectItemProgressIndicatorContent,
  SelectItemInput,
  SelectItemBaseText,
  SelectItemBaseIcon,
  SelectItemSubText,
  SelectItemTextContainer,
};

export default styles;
